import React from "react";
import { Check, Clock3 } from "lucide-react";

const STOPS = [
  { key: "account", label: "Account" },
  { key: "practice", label: "Practice" },
  { key: "campaign", label: "Campaign" },
  { key: "launch", label: "Launch" },
];

/**
 * Shared shell for the onboarding flow.
 *
 * App-mode principles:
 *  - One task per screen, one primary action
 *  - Sticky compact progress rail with "Step X of 4"
 *  - Optional eyebrow + estimated-time chip prime the user for effort
 *  - Optional next-step hint reduces "what happens next" anxiety
 *  - Left-aligned typography, max content width tuned per screen
 */
export default function OnboardingLayout({
  currentStep = "account",
  eyebrow,
  title,
  subtitle,
  estimatedMinutes,
  children,
  footer,
  hideRail = false,
  maxWidth = "max-w-2xl",
}) {
  const activeIdx = STOPS.findIndex((s) => s.key === currentStep);
  const stepPosition = activeIdx >= 0 ? activeIdx + 1 : null;

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-background flex flex-col">
      {!hideRail && (
        <div className="border-b border-border/60 bg-background/60 backdrop-blur">
          <div className="max-w-4xl mx-auto px-6 py-3.5 flex items-center gap-6">
            <div className="hidden sm:flex items-center gap-2 shrink-0 text-xs font-sans-ui uppercase tracking-widest text-muted-foreground">
              <span data-testid="onboarding-step-counter">
                Step {stepPosition} of {STOPS.length}
              </span>
            </div>
            <ol
              className="flex items-center gap-3 sm:gap-4 flex-1"
              data-testid="onboarding-progress-rail"
            >
              {STOPS.map((s, i) => {
                const done = i < activeIdx;
                const active = i === activeIdx;
                return (
                  <li
                    key={s.key}
                    className="flex items-center gap-3 sm:gap-3 flex-1 last:flex-initial"
                  >
                    <div
                      className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center text-xs font-semibold transition-colors ${
                        done
                          ? "bg-accent border-accent text-accent-foreground"
                          : active
                          ? "border-accent text-accent"
                          : "border-border/60 text-muted-foreground"
                      }`}
                      data-testid={`onboarding-step-${s.key}${
                        active ? "-active" : done ? "-done" : ""
                      }`}
                    >
                      {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <span
                      className={`hidden md:inline text-sm font-sans-ui tracking-wide ${
                        active
                          ? "text-foreground"
                          : done
                          ? "text-muted-foreground"
                          : "text-muted-foreground/70"
                      }`}
                    >
                      {s.label}
                    </span>
                    {i < STOPS.length - 1 && (
                      <div className="hidden sm:block h-px flex-1 bg-border/60" />
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}
      <div className="flex-1 flex items-start justify-center px-6 py-10 sm:py-14">
        <div className={`w-full ${maxWidth}`}>
          {(eyebrow || title || subtitle || estimatedMinutes) && (
            <div className="mb-8">
              {eyebrow && (
                <div
                  className="text-xs font-sans-ui uppercase tracking-[0.18em] text-accent/90 mb-3"
                  data-testid="onboarding-eyebrow"
                >
                  {eyebrow}
                </div>
              )}
              {title && (
                <h1
                  className="font-display text-3xl sm:text-4xl leading-tight text-foreground"
                  data-testid="onboarding-title"
                >
                  {title}
                </h1>
              )}
              {(subtitle || estimatedMinutes) && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  {subtitle && (
                    <p
                      className="text-base text-muted-foreground max-w-xl"
                      data-testid="onboarding-subtitle"
                    >
                      {subtitle}
                    </p>
                  )}
                  {estimatedMinutes && (
                    <span
                      className="inline-flex items-center gap-1.5 text-xs font-sans-ui text-muted-foreground rounded-full border border-border/60 bg-card/40 px-2.5 py-1"
                      data-testid="onboarding-eta"
                    >
                      <Clock3 className="w-3 h-3" /> {estimatedMinutes}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          <div>{children}</div>
          {footer && <div className="mt-8">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
